import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from "react-redux";
import { AppActionCreators, IApplicationState, IOverallPerformanceListResponse, ISystemConfigResponse, ReportActionCreators, SystemConfigActionCreators } from '@nkr/store/index';
import { IRelativePerformance } from '@nkr/models/index';
import { GridApi, ColumnApi, GridReadyEvent, FirstDataRenderedEvent, CsvExportParams, ProcessHeaderForExportParams, ProcessCellForExportParams } from 'ag-grid-community';
import { Button, FormControl, Glyphicon, OverlayTrigger } from 'react-bootstrap';
import Info from './info';
import Grid from './grid';

interface IOwnProps {
    isCenterLogin: boolean
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getOverallPerformanceList: (lvPercentile: number, nvPercentile: number) => Promise<IOverallPerformanceListResponse>,
    getSystemConfig: (list: number[]) => Promise<ISystemConfigResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: IRelativePerformance[],
    centerId: number,
    lvRank: number,
    nvRank: number,
    gridApi: GridApi,
    columnApi: ColumnApi
}

class OverallPerformance extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        centerId: 0,
        lvRank: 0,
        nvRank: 0,
        gridApi: null,
        columnApi: null
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api,
            columnApi: e.columnApi
        }, () => {
            e.columnApi.applyColumnState({
                state: [
                    {
                        colId: 'overallRank',
                        sort: 'asc' // 'asc'
                    }
                ]
            });
        });
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting overall performance, one moment please...').then(async () => {
            let configResponse = await this.props.getSystemConfig([1, 2]);
            let lvRankItem = configResponse.data.results.find(x => x.configItemId == 1);
            let nvRankItem = configResponse.data.results.find(x => x.configItemId == 2);

            let lvRank = parseInt(lvRankItem.value);
            let nvRank = parseInt(nvRankItem.value);

            let dataResponse = await this.props.getOverallPerformanceList(lvRank, nvRank);

            this.setState({
                lvRank,
                nvRank,
                data: dataResponse.data.results
            }, () => {
                this.props.hideLoading();
            });
        });
    }

    updateReport = () => {
        let { lvRank, nvRank } = this.state;

        this.props.showLoading('Updating overall performance, one moment please...').then(async () => {
            let dataResponse = await this.props.getOverallPerformanceList(lvRank, nvRank);

            this.setState({
                data: dataResponse.data.results
            }, () => {
                this.props.hideLoading();
            });
        });
    }

    handleLvRankChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            lvRank: parseInt(e.currentTarget.value)
        });
    }

    handleNvRankChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            nvRank: parseInt(e.currentTarget.value)
        });
    }

    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerId: parseInt(e.currentTarget.value)
        });
    }

    handleSubmit = () => {
        this.updateReport();
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'overall-performance-by-rank.csv',
            processHeaderCallback: (params: ProcessHeaderForExportParams) => {
                let headerName = params.column.getColDef().headerName;
                return headerName.replace(/\|/gi, ' ');
            },
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();
                let data = params.node.data as IRelativePerformance;

                if (colDef.colId == 'preselectsAverageReviewedRank') {
                    return this.renderExportValue(data.preselectsAveragePercentReviewedThisMonth.category, data.preselectsAveragePercentReviewedThisMonth.rank);
                }

                if (colDef.colId == 'preselectsAverageReviewed') {
                    return this.renderExportValue(data.preselectsAveragePercentReviewedThisMonth.category, data.preselectsAveragePercentReviewedThisMonth.average);
                }

                if (colDef.colId == 'surgeryDateDeclinesRank') {
                    return this.renderExportValue(data.surgeryDateDecline.category, data.surgeryDateDecline.rank);
                }

                if (colDef.colId == 'surgeryDateDeclines') {
                    return this.renderExportValue(data.surgeryDateDecline.category, data.surgeryDateDecline.declineRate);
                }

                if (colDef.colId == 'swapFailureRank') {
                    return this.renderExportValue(data.swapFailureRate.category, data.swapFailureRate.rank);
                }

                if (colDef.colId == 'swapFailure') {
                    return this.renderExportValue(data.swapFailureRate.category, data.swapFailureRate.failureRate);
                }

                if (colDef.colId == 'chainEndDeclineRatioRank') {
                    return this.renderExportValue(data.chainEndDeclineRatio.category, data.chainEndDeclineRatio.rank);
                }

                if (colDef.colId == 'chainEndDeclineRatio') {
                    return this.renderExportValue(data.chainEndDeclineRatio.category, data.chainEndDeclineRatio.declineRatio);
                }

                if (colDef.colId == 'nkrDonorReferralConversionRateRank') {
                    return this.renderExportValue(data.nkrDonorReferralConversionRate.category, data.nkrDonorReferralConversionRate.rank);
                }

                if (colDef.colId == 'nkrDonorReferralConversionRate') {
                    return this.renderExportValue(data.nkrDonorReferralConversionRate.category, data.nkrDonorReferralConversionRate.conversionRate);
                }

                if (colDef.colId == 'voucherDisconnectRank') {
                    return this.renderExportValue(data.voucherDisconnect.category, data.voucherDisconnect.rank);
                }

                if (colDef.colId == 'voucherDisconnect') {
                    return this.renderExportValue(data.voucherDisconnect.category, data.voucherDisconnect.percentDisconnect);
                }

                if (colDef.colId == 'missedMatchOfferDeadlineRank') {
                    return this.renderExportValue(data.missedMatchOfferDeadline.category, data.missedMatchOfferDeadline.rank);
                }

                if (colDef.colId == 'missedMatchOfferDeadline') {
                    return this.renderExportValue(data.missedMatchOfferDeadline.category, data.missedMatchOfferDeadline.ratio);
                }

                if (colDef.colId == 'svDonationsRank') {
                    return this.renderExportValue(data.svDonations.category, data.svDonations.rank);
                }

                if (colDef.colId == 'svDonations') {
                    return this.renderExportValue(data.svDonations.category, data.svDonations.totalStandardVouchers);
                }

                if (colDef.colId == 'fvDonationsRank') {
                    return this.renderExportValue(data.fvDonations.category, data.fvDonations.rank);
                }

                if (colDef.colId == 'fvDonations') {
                    return this.renderExportValue(data.fvDonations.category, data.fvDonations.totalFamilyVouchers);
                }

                if (colDef.colId == 'kflSuccessRateRank') {
                    return this.renderExportValue(data.kflSuccessRate.category, data.kflSuccessRate.rank);
                }

                if (colDef.colId == 'kflSuccessRate') {
                    return this.renderExportValue(data.kflSuccessRate.category, data.kflSuccessRate.kidneyForLifeSuccessRate);
                }

                if (colDef.colId == 'micrositeEnrollmentSuccessRateRank') {
                    return this.renderExportValue(data.micrositeEnrollmentRate.category, data.micrositeEnrollmentRate.rank);
                }

                if (colDef.colId == 'micrositeEnrollmentSuccessRate') {
                    return this.renderExportValue(data.micrositeEnrollmentRate.category, data.micrositeEnrollmentRate.micrositeEnrollmentRate);
                }

                if (colDef.colId == 'micrositePatientReferralSuccessRateRank') {
                    return this.renderExportValue(data.micrositePatientReferralSuccessRate.category, data.micrositePatientReferralSuccessRate.rank);
                }

                if (colDef.colId == 'micrositePatientReferralSuccessRate') {
                    return this.renderExportValue(data.micrositePatientReferralSuccessRate.category, data.micrositePatientReferralSuccessRate.conversionRate);
                }

                if (colDef.colId == 'fiveStarSurveyResultsRank') {
                    return this.renderExportValue(data.fiveStarSurveyResults.category, data.fiveStarSurveyResults.rank);
                }

                if (colDef.colId == 'fiveStarSurveyResults') {
                    return this.renderExportValue(data.fiveStarSurveyResults.category, data.fiveStarSurveyResults.average);
                }

                if (colDef.colId == 'cprScoreRank') {
                    return this.renderExportValue(data.cprScore.category, data.cprScore.rank);
                }

                if (colDef.colId == 'cprScore') {
                    return this.renderExportValue(data.cprScore.category, data.cprScore.cprScore);
                }

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    renderExportValue = (category: string, value: number) => {
        if (category == 'LV') {
            return `LV (${value})`;
        }

        if (category == 'NV') {
            return `NV (${value})`;
        }

        return value;
    }

    render = () => {
        const filteredData = this.state.data.filter(x => {
            let center = this.state.centerId > 0 ? x.centerId == this.state.centerId : true;
            return center;
        });

        const centerList = _.sortBy(_.uniqBy(this.state.data.map(item => {
            return { id: item.centerId, alias: item.centerAlias };
        }), item => item.id), item => item.alias);

        const enableSubmit = true;

        return (
            <>
                {this.props.isCenterLogin == false && (
                    <>
                        <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                            <div style={{ fontSize: 28, fontWeight: 500 }}>Overall Performance by Rank</div>
                            <div>

                            </div>
                            <div>
                                <OverlayTrigger placement='left' trigger='click' overlay={<Info lvPercentile={this.state.lvRank} nvPercentile={this.state.nvRank} />} rootClose>
                                    <div className='text-info'>
                                        <Glyphicon glyph='info-sign' style={{ fontSize: 18 }} />
                                    </div>
                                </OverlayTrigger>
                            </div>
                        </div>

                        <div className='well well-sm' style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>Low Volume Rank:</div>
                                    <div style={{ marginRight: 20 }}>
                                        <FormControl
                                            style={{ width: 50 }}
                                            value={this.state.lvRank}
                                            onChange={this.handleLvRankChange}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>No Volume Rank:</div>
                                    <div style={{ marginRight: 20 }}>
                                        <FormControl
                                            style={{ width: 50 }}
                                            value={this.state.nvRank}
                                            onChange={this.handleNvRankChange}
                                        />
                                    </div>
                                </div>

                                <Button bsStyle='primary' disabled={!enableSubmit} onClick={this.handleSubmit}>Update Report</Button>
                            </div>

                            <div style={{ display: "flex", alignItems: "center" }}>
                                <div style={{ marginRight: 5 }}>Filter by Center:</div>
                                <div style={{ marginRight: 20 }}>
                                    <FormControl
                                        componentClass='select'
                                        value={this.state.centerId}
                                        onChange={this.handleCenterChange}>
                                        <option value={0}>- All Centers -</option>
                                        {centerList.map(center =>
                                            <option key={`ctr-${center.id}`} value={center.id}>{center.alias}</option>
                                        )}
                                    </FormControl>
                                </div>
                            </div>

                            <div>
                                <Button onClick={this.handleExport}>Export to CSV/Excel</Button>
                            </div>
                        </div>
                    </>
                )}

                {/* <ReportWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        rowData={filteredData}
                        columnDefs={this.state.columns}
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true,
                            resizable: false,
                        }}
                        headerHeight={66}
                        groupHeaderHeight={30}
                        rowHeight={30}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        applyColumnDefOrder={true}
                        noRowsOverlayComponentFramework={() => <div>No results found...</div>}
                        onGridReady={this.onGridReady}
                        onFirstDataRendered={this.onFirstDataRendered}
                    // onSortChanged={(e: SortChangedEvent) => {

                    // }}
                    />
                </ReportWrapper> */}

                <Grid
                    data={filteredData}
                    onGridReady={this.onGridReady}
                    onFirstDataRendered={this.onFirstDataRendered} />

                {this.props.isCenterLogin == true && (
                    <div style={{ marginTop: 5, display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                        <div>{this.state.data.length} centers...</div>
                        <div>
                            <Button onClick={this.handleExport}>Export to CSV/Excel</Button>
                        </div>
                    </div>
                )}
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getOverallPerformanceList: (lvPercentile, nvPercentile) => {
            return dispatch(ReportActionCreators.getOverallPerformanceList(lvPercentile, nvPercentile));
        },
        getSystemConfig: (list) => {
            return dispatch(SystemConfigActionCreators.getByList(list));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(OverallPerformance);